import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from "react";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import {
  subscribeNotifications,
  markNotificationRead,
  markNotificationsRead,
  deleteNotification,
  clearNotifications,
} from "../utils/notifications";

// App-wide in-app notifications. One live subscription per signed-in user feeds
// the bell + count on the home screen, the "Address updated" badge on changed
// events, and the banner on the event screen. Consumers use useNotifications().
const NotificationsContext = createContext({
  notifications: [],
  unreadCount: 0,
  unreadByCode: {},
  markRead: () => {},
  markAllRead: () => {},
  markEventRead: () => {},
  remove: () => {},
  clearAll: () => {},
});

export const useNotifications = () => useContext(NotificationsContext);

export const NotificationsProvider = ({ children }) => {
  const [uid, setUid] = useState(null);
  const [notifications, setNotifications] = useState([]);

  useEffect(() => {
    const auth = getAuth();
    const unsub = onAuthStateChanged(auth, (u) => setUid(u ? u.uid : null));
    return () => unsub();
  }, []);

  useEffect(() => {
    if (!uid) {
      setNotifications([]);
      return undefined;
    }
    const unsub = subscribeNotifications(uid, setNotifications);
    return () => unsub();
  }, [uid]);

  const unreadCount = useMemo(
    () => notifications.filter((n) => !n.read).length,
    [notifications],
  );

  // How many unread notifications each event (code) has - powers the per-event
  // "Address updated" badge in the events list and the banner on the event screen.
  const unreadByCode = useMemo(() => {
    const map = {};
    notifications.forEach((n) => {
      if (!n.read && n.code) map[n.code] = (map[n.code] || 0) + 1;
    });
    return map;
  }, [notifications]);

  const markRead = useCallback(
    (id) => {
      if (uid) markNotificationRead(uid, id);
    },
    [uid],
  );

  const markAllRead = useCallback(() => {
    if (!uid) return;
    const ids = notifications.filter((n) => !n.read).map((n) => n.id);
    markNotificationsRead(uid, ids);
  }, [uid, notifications]);

  // Mark every unread notification for one event read - called when the user
  // opens that event, so seeing the new address clears its badge/count.
  const markEventRead = useCallback(
    (code) => {
      if (!uid) return;
      const ids = notifications
        .filter((n) => !n.read && n.code === code)
        .map((n) => n.id);
      markNotificationsRead(uid, ids);
    },
    [uid, notifications],
  );

  const remove = useCallback(
    (id) => {
      if (uid) deleteNotification(uid, id);
    },
    [uid],
  );

  const clearAll = useCallback(() => {
    if (!uid) return;
    clearNotifications(
      uid,
      notifications.map((n) => n.id),
    );
  }, [uid, notifications]);

  const value = useMemo(
    () => ({
      notifications,
      unreadCount,
      unreadByCode,
      markRead,
      markAllRead,
      markEventRead,
      remove,
      clearAll,
    }),
    [
      notifications,
      unreadCount,
      unreadByCode,
      markRead,
      markAllRead,
      markEventRead,
      remove,
      clearAll,
    ],
  );

  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  );
};
